import PropTypes from 'prop-types';
import {useState} from 'react';
import useLabels from 'infrastructure/js/hooks/useLabels';
import cn from 'classnames';
import NameValuePairs from '../../../Common/NameValuePairs/nameValuePairs';
import TextAreaField from 'infrastructure/js/components/controls/TextAreaField/textAreaField';
import InputSection from 'infrastructure/js/components/Dialog/InputSection/inputSection';
import {enumTypes, getEnumLabel, getEnumValue} from '../../../../utils/enumHelper';
import TextField from 'infrastructure/js/components/controls/TextField/textField';
import CocRangeBar from '../../CoCRangeBar/cocRangeBar';
import Normalize from 'infrastructure/js/components/controls/controlsNormalizations';
import CoCAttachments from '../CoCAttachments/cocAttachments';
import {getCoCResultOptions, ResultStatus} from '../../utils/cocHelper';
import {QPL_MAX_DECIMAL, QPL_MIN_DECIMAL} from '../../../Administration/MatSettingsPage/utils/qplHelper';
import RadioButton from 'infrastructure/js/components/controls/RadioButton/radioButton';
import ButtonWithTooltip from 'infrastructure/js/components/controls/ButtonWithTooltip/buttonWithTooltip';
import CocTestResultIndicator from '../../CoCTestResultIndicator/cocTestResultIndicator';
import CocAcceptanceDecisions from '../CoCAcceptanceDecisions/cocAcceptanceDecisions';

import './cocSectionListItem.scss';

const isQualityTest = testType => testType === getEnumValue(enumTypes.TEST_TYPE)('QUALITY');

const isEvenColumn = columnOrder => columnOrder % 2 === 0;

//for Quantity test's raw materials (checks if it is 'Roll name' column
const isSkippedValueColumn = (cocData, item) => cocData?.passCriteria && !isEvenColumn(item.columnOrder);

const isRequestButtonEnabled = (cocData) => {
  const {acceptanceCycleCount, maxAcceptanceCycles, testResult} = cocData ?? {};
  return (
    acceptanceCycleCount < maxAcceptanceCycles && (!testResult || testResult=== getEnumValue(enumTypes.COC_TEST_RESULT)('FAIL'))
  );
}

const isEmptyValue = (item, reduxData, isResultField = false) => {
  const raw = reduxData.rawMaterials?.[`id-${item.id}`];
  return raw === null || raw === undefined || raw === '' || (isResultField && isNaN(raw));
}

const getValidNumericValue = (item, reduxData) => {
  const raw = reduxData.rawMaterials?.[`id-${item.id}`];
  if (raw === null || raw === undefined || raw === '' || isNaN(raw)) {
    return null;
  }
  return +raw;
};

const getFieldValueStatus = (item, cocData = {}, reduxData = {}) => {
  if (isSkippedValueColumn(cocData, item)) {
    return '';
  }

  const value = getValidNumericValue(item, reduxData);
  if (value === null) {
    return '';
  }

  const {minimumValue, maximumValue} = cocData;
  const min = minimumValue ?? QPL_MIN_DECIMAL;
  const max = maximumValue ?? QPL_MAX_DECIMAL;

  return (value >= min && value <= max) ? 'pass' : 'fail';
};

const getResultsStatus = (results, minValue, maxValue) => {
  if (!Array.isArray(results) || results.length === 0) {
    return {
      status: '',
      minOut: null,
      maxOut: null,
    };
  }

  const min = minValue ?? QPL_MIN_DECIMAL;
  const max = maxValue ?? QPL_MAX_DECIMAL;

  let hasMissing = false;
  let hasValidNumbers = false;
  let minOut = null;
  let maxOut = null;

  for (const item of results) {
    if (item === null || item === undefined || item === '') {
      hasMissing = true;
      continue;
    }

    const num = Number(item);
    if (isNaN(num)) {
      hasMissing = true;
      continue;
    }

    hasValidNumbers = true;

    if (num < min) {
      minOut = minOut === null ? num : Math.min(minOut, num);
      continue;
    }
    if (num > max) {
      maxOut = maxOut === null ? num : Math.max(maxOut, num);
    }
  }

  const hasOutOfRange = minOut !== null || maxOut !== null;

  if (hasOutOfRange) {
    return {
      status: hasMissing ? ResultStatus.PARTIAL_FAIL : ResultStatus.FAIL,
      minOut,
      maxOut,
    };
  }

  return {
    status: !hasValidNumbers ? '' : (hasMissing ? ResultStatus.PARTIAL_PASS : ResultStatus.PASS),
    minOut: null,
    maxOut: null,
  };
};

const getRangeResultStatus = (cocData = {}, reduxData = {}) => {
  const {rawMaterialsResultFieldsIds = [], minimumValue, maximumValue} = cocData;
  const rawMaterials = reduxData?.rawMaterials || {};

  const results = rawMaterialsResultFieldsIds.map(id => rawMaterials[`id-${id}`]);

  return getResultsStatus(results, minimumValue, maximumValue);

};

const renderQuantityRows = (row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors) => {
  return row.map((item, columnIndex) => {
    const itemId = `id-${item.id}`;

    const statusClass = getFieldValueStatus(item, cocData, reduxData);

    const normalizeFn = isSkippedValueColumn(cocData, item) ? undefined :
      Normalize.normalizeFloat(QPL_MIN_DECIMAL, QPL_MAX_DECIMAL, 5);

    const isResultField = cocData?.rawMaterialsResultFieldsIds?.includes(item.id);
    const emptyErrorClass = (showEmptyFieldErrors && isEmptyValue(item, reduxData, isResultField)) ? 'empty-error' : '';

    return (
      <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
        <span className="raw-material-label">{item.displayName}</span>
        <TextField
          className={`raw-material-value ${statusClass} ${emptyErrorClass}`.trim()}
          id={`${name}.[rawMaterials][${item.id}]`}
          name={`${name}.[rawMaterials].${itemId}`}
          normalize={normalizeFn}
          disabled={isViewOnly}
        />
      </div>
    );
  });
}

const renderQualityRows = (row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors) => {
  return row.map((item, columnIndex) => {
    const itemId = `id-${item.id}`;

    const emptyErrorClass = (showEmptyFieldErrors && isEmptyValue(item, reduxData)) ? 'empty-error' : '';

    return (
      <div key={`key-${index}-${rowIndex}-${columnIndex}`} className="row-material">
        <span className="raw-material-label">{item.displayName}</span>

        {isEvenColumn(item.columnOrder) ?
          <RadioButton
            className={`${emptyErrorClass}`}
            id={`${name}.[rawMaterials][${item.id}]`}
            name={`${name}.[rawMaterials].${itemId}`}
            labelsAndValues={getCoCResultOptions()}
            disabled={isViewOnly}
          />
          :
          <TextField
            className={`raw-material-value ${emptyErrorClass}`.trim()}
            id={`${name}.[rawMaterials][${item.id}]`}
            name={`${name}.[rawMaterials].${itemId}`}
            disabled={isViewOnly}
          />}
      </div>)
  });
}

const renderRawMaterials = (testType, cocData, reduxData, index, name, isViewOnly, showEmptyFieldErrors) => {
  const rawMaterialsFields = cocData?.rawMaterialsFields ?? [];
  if (rawMaterialsFields.length === 0) {
    return null;
  }

  const isQuality = isQualityTest(testType);
  const renderRowsMethod = isQuality ? renderQualityRows : renderQuantityRows;

  return (
    <div className="raw-materials-results">
      {rawMaterialsFields.map((row, rowIndex) => (
        <div key={`row-key-${index}-${rowIndex}`} className={cn('raw-material-row', {'quality-row': isQuality})}>
          {renderRowsMethod(row, rowIndex, index, name, cocData, reduxData, isViewOnly, showEmptyFieldErrors)}
        </div>
      ))}
    </div>
  );
}

const renderOptionalFieldsSection = (cocData) => {
  const hasOptionalFields = cocData?.optionalFields?.length > 0;
  return hasOptionalFields ? (
    <NameValuePairs className='optional-fields' items={cocData?.optionalFields}/>
  ) : null;
}

const renderComment = (labels, cocData, index, name, isViewOnly) => {
  const hasComment = cocData?.hasComment;

  return hasComment ? (
    <InputSection label={labels.get('comment')} htmlFor="comment" className="comment-section">
      <TextAreaField id={`${name}.[comment]`} name={`${name}.[comment]`} className="comment-textField" maxLength="250" disabled={isViewOnly}/>
    </InputSection>
  ) : null;
}

const renderCoCRangeBar = (labels, cocData, reduxData) => {
  const rangeResult = getRangeResultStatus(cocData, reduxData);

  return <CocRangeBar label={labels.get('passCriteria')}
                      min={cocData?.minimumValue}
                      max={cocData?.maximumValue}
                      minUnit={cocData?.minUnit}
                      maxUnit={cocData?.maxUnit}
                      {...rangeResult}
  />
}

const createCoCSectionData = (labels, cocData) => {
  const sectionData = [ {label: labels.get('set'), value: cocData.setName} ];

  if (cocData.rawMaterialsTested) {
    sectionData.push({label: labels.get('rawMaterialsTested'), value: cocData.rawMaterialsTested});
  }
  if (cocData.passCriteria) {
    sectionData.push({label: labels.get('overallPassCriteria'), value: getEnumLabel(enumTypes.PASS_CRITERIA)(cocData.passCriteria)});
  }

  return sectionData;
}


const CoCSectionListItem = ({
                              name,
                              index: sectionIndex,
                              cocData= {},
                              reduxData = {},
                              attachmentsCounters = [],
                              attachmentsFiles = [],
                              onAttachmentUpload = null,
                              onAttachmentUploadValidation = null,
                              onAttachmentDownload = null,
                              onAttachmentRemove = null,
                              onAttachmentClick = null,
                              onAttachmentResultChange = null,
                              onAcceptanceRequestClick = null,
                              onAcceptanceDecisionsClick = null,
                              isViewOnly = false,
                              testType,
                              showEmptyFieldErrors = false,
                            }) => {

  const labels = useLabels('mat.forms.cocTestForm.sectionListItem.');
  const [showAcceptanceDecisions, setShowAcceptanceDecisions] = useState(false);
  const [acceptanceDecisions, setAcceptanceDecisions] = useState(null);
  const [isAcceptanceDecisionsLoading, setIsAcceptanceDecisionsLoading] = useState(false);

  const cocSectionData = createCoCSectionData(labels, cocData);

  //Handlers
  const onAttachmentUploadHandler = (file, type) => {
    onAttachmentUpload?.(file, type, cocData.setId, sectionIndex)
  }

  const onAttachmentRemoveHandler = (file) => {
    onAttachmentRemove?.(file, sectionIndex);
  }

  const onAttachmentClickHandler = (file) => {
    onAttachmentClick?.(file, sectionIndex);
  }

  const onAttachmentResultChangeHandler = (value, file) => {
    onAttachmentResultChange?.(value, file, sectionIndex);
  }

  const onAcceptanceRequestClickHandler = (e) => {
    e?.currentTarget?.blur();  //note: to remove the button's tool tip
    const {setId, setName} = cocData ?? {};
    onAcceptanceRequestClick?.({setId, setName, sectionIndex});
  }

  const onAcceptanceDecisionsCallback = (data, setIndex) => {
    setShowAcceptanceDecisions(true);
    setAcceptanceDecisions(data);
    setIsAcceptanceDecisionsLoading(false);
  }


  const onAcceptanceDecisionsClickHandler = () => {
    if (showAcceptanceDecisions) {
      setShowAcceptanceDecisions(false);
      return;
    }

    setAcceptanceDecisions(null);   //remove previuos items if exists
    setShowAcceptanceDecisions(true);
    setIsAcceptanceDecisionsLoading(true);

    const {setId, setName} = cocData ?? {};
    onAcceptanceDecisionsClick?.({setId, setName, sectionIndex, callback: onAcceptanceDecisionsCallback});
  }

  // Disable drag/drop events anywhere in this component so the browser doesn't
  // fall back to its native "open/save file" behavior when a file is dropped here.
  const onContainerDragOverHandler = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'none'
  }

  const onContainerDropHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
  }

  //Renderers
  const renderCustomerAcceptanceSection = (sectionIndex) => {
    return (
      <div className='coc-customer-acceptance-section'>
        <ButtonWithTooltip id={`acceptanceDecisionsBtn-${sectionIndex}`}
                           icon="pl pl-list"
                           className="border-btn only-icon"
                           onClick={onAcceptanceDecisionsClickHandler}
                           tooltip={labels.get('button.showCustomerDecisions')}
        />
        <ButtonWithTooltip id={`acceptanceRequestBtn-${sectionIndex}`}
                           icon="pl pl-test-exclusion-request"
                           className="border-btn only-icon"
                           tooltip={labels.get('button.requestCustomerAcceptance')}
                           onClick={onAcceptanceRequestClickHandler}
                           disabled={isViewOnly || !isRequestButtonEnabled(cocData)}
        />
      </div>
    )
  }

  const renderRawMaterialsSection = () => {
    const hasRawMaterials = cocData?.rawMaterialsResultFieldsIds?.length > 0;
    if (!hasRawMaterials) {
      return null;
    }

    return (
      <div className='raw-materials-section'>
        {isQualityTest(testType) ? null : renderCoCRangeBar(labels, cocData, reduxData)}
        {renderRawMaterials(testType, cocData, reduxData, sectionIndex, name, isViewOnly, showEmptyFieldErrors)}
      </div>
    )
  }

  return (
    <div className='coc-section-list-item'
         onDragOver={onContainerDragOverHandler}
         onDrop={onContainerDropHandler}
    >

      <div className='top-section'>
        <NameValuePairs items={cocSectionData}/>

        <div className='top-section-controls'>
          <CocTestResultIndicator result={cocData.testResult}/>
          {renderCustomerAcceptanceSection(sectionIndex)}
        </div>
      </div>

      <div className={cn('acceptance-decisions-anchor', {open: showAcceptanceDecisions})}>
        <div className="acceptance-decisions-arrow"/>
        <div className="acceptance-decisions-wrapper">
          <CocAcceptanceDecisions items={acceptanceDecisions} onFileClick={onAttachmentDownload} loading={isAcceptanceDecisionsLoading} />
        </div>
      </div>

      {renderOptionalFieldsSection(cocData)}

      {renderRawMaterialsSection()}

      {renderComment(labels, cocData, sectionIndex, name, isViewOnly)}

      <div className="attachments-section">
        <CoCAttachments
          index={sectionIndex} name={name}
          isViewOnly={isViewOnly}
          attachmentsCounters={attachmentsCounters}
          attachmentsFiles={attachmentsFiles}
          onAttachmentUpload={onAttachmentUploadHandler}
          onAttachmentUploadValidation={onAttachmentUploadValidation}
          onAttachmentRemove={onAttachmentRemoveHandler}
          onAttachmentClick={onAttachmentClickHandler}
          onAttachmentDownload={onAttachmentDownload}
          onAttachmentResultChange={onAttachmentResultChangeHandler}
          genericAttachmentsRequired={cocData.genericAttachmentsRequired}
          showEmptyFieldErrors={showEmptyFieldErrors}
        />
      </div>
    </div>
  )
}

CoCSectionListItem.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  cocData: PropTypes.object,
  reduxData: PropTypes.object,
  attachmentsCounters: PropTypes.array,
  attachmentsFiles: PropTypes.array,
  onAttachmentUpload: PropTypes.func,
  onAttachmentUploadValidation: PropTypes.func,
  onAttachmentDownload: PropTypes.func,
  onAttachmentRemove: PropTypes.func,
  onAttachmentClick: PropTypes.func,
  onAttachmentResultChange: PropTypes.func,
  onAcceptanceRequestClick: PropTypes.func,
  onAcceptanceDecisionsClick: PropTypes.func,
  isViewOnly: PropTypes.bool,
  testType: PropTypes.string,
  showEmptyFieldErrors: PropTypes.bool,
};

export default CoCSectionListItem;
